import {
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { GasBind, GasInputBoolean } from '@cikrf/gas-utils/decorators';
import { BehaviorSubject } from 'rxjs';

const GAS_UPLOADER = 'GAS-UPLOADER';

@Directive({
  selector: '[gasUploader], gas-uploader',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GasUploaderDirective),
      multi: true,
    },
  ],
  host: { class: 'g-cursor-p' },
})
export class GasUploaderDirective implements OnInit, ControlValueAccessor, OnDestroy {
  @Output('onChangeFile')
  public onChangeFile$ = new EventEmitter<File | File[]>();

  @Input()
  public type = 'all';

  @GasInputBoolean()
  @Input()
  public isDragAndDrop: string | boolean = false;

  @Input()
  public activeDraggedClass = '_dragged';

  @Input()
  public clickable = false;

  @Input()
  public maxFileSize: number;

  /**
   * Требуется указывать MIME тип
   */
  @Input()
  public accept = '';

  @Input()
  public multi = false;

  public errorSubject$ = new BehaviorSubject<boolean>(false);

  private input: HTMLInputElement;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {
    this.elementRef.nativeElement.draggable = true;
  }

  @HostListener('click')
  public upload(): void {
    if (!this.clickable) {
      return;
    }

    this.input.click();
  }

  @GasBind()
  public dragOverFile(event: DragEvent): void {
    if (!this.isDragAndDrop) {
      return;
    }

    event.preventDefault();

    if (( this.multi && event.dataTransfer?.items?.length > 1 ) || ( !this.multi && event.dataTransfer?.items?.length !== 1 )) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }

    // @ts-ignore
    if (this.accept && [...event.dataTransfer?.items]?.some((item: DataTransferItem) => !item.type || !this.accept.includes(item.type))) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }

    event.stopPropagation();

    this.renderer.addClass(this.elementRef.nativeElement, this.activeDraggedClass);
  }

  @GasBind()
  public dragLeaveFile(event: DragEvent): void {
    if (!this.isDragAndDrop) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.renderer.removeClass(this.elementRef.nativeElement, this.activeDraggedClass);
  }

  @GasBind()
  public dropFile(event: DragEvent): void {
    if (!this.isDragAndDrop) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.uploadFile(event, true);
    this.renderer.removeClass(this.elementRef.nativeElement, this.activeDraggedClass);
  }

  /**
   * метод получает загруженный файл и сообщает через метод onChange
   * что значение изменилось
   */
  @GasBind()
  public uploadFile(event: any, isDrag: boolean = false): void {
    let file: File;

    if (isDrag) {
      file = event.dataTransfer?.files[0];
    } else {
      file = event.target?.files[0];
    }

    if (!file || !this.checkFileType(this.type, file)) {
      return;
    }

    const filesArray = this.getFileArray(event.dataTransfer?.files, event.target?.files);

    this.onChangeFile$.emit(filesArray);
    this.onChange(this.multi ? filesArray : file);
    this.input.value = '';
  }

  public ngOnInit(): void {
    /**
     * так как тут используется мультиселектор и директива автоматически будет навешиваться на компонент gas-uploader
     * нужно как то обозначить, это он должен быть dragAndDrop, этим заниматеся метод setIsDragAndDropValue, но
     * заходить в метод надо только если isDragAndDrop равен false, что бы не переназначать свойство там, где это не нужно
     */
    if (!this.isDragAndDrop) {
      this.setIsDragAndDropValue();
    }

    this.createInputFile();

    if (this.isDragAndDrop) {
      this.elementRef.nativeElement.addEventListener('dragover', this.dragOverFile);
      this.elementRef.nativeElement.addEventListener('dragleave', this.dragLeaveFile);
      this.elementRef.nativeElement.addEventListener('drop', this.dropFile);
    }
  }

  public selectFile(): void {
    if (!this.input) {
      return;
    }

    this.input.click();
  }

  public ngOnDestroy(): void {
    this.input.remove();

    if (this.isDragAndDrop) {
      this.elementRef.nativeElement.removeEventListener('dragover', this.dragOverFile);
      this.elementRef.nativeElement.removeEventListener('dragleave', this.dragLeaveFile);
      this.elementRef.nativeElement.removeEventListener('drop', this.dropFile);
    }
  }

  public writeValue(value: File | File[]): void {
    this.onChange(value);
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  /**
   * Данный метод создает и помещает в конец body тег <input type='file'>
   * ставит ему display = 'none'и навешивает обработчик события input
   * далее при загрузке файла срабатывает метод uploadFile
   */
  public createInputFile(): void {
    this.input = document.createElement('input');
    this.input.setAttribute('type', 'file');
    if (this.multi) {
      this.input.setAttribute('multiple', '');
    }
    this.input.style.display = 'none';
    this.input.setAttribute('accept', this.accept);
    this.elementRef.nativeElement.appendChild(this.input);
    this.input.addEventListener('input', this.uploadFile);
  }

  /**
   * Проверяет загружаемый файл на соответсвие типу расширения
   * если тип не был передан будет возвращаться true
   * если тип передан то происходит проверка
   */
  public checkFileType(type: string, file: File): boolean {
    if (type === 'all') {
      return true;
    }

    const fileType = file.type.split('/')[1];
    const userType = type.split('/');

    if (userType.includes(fileType)) {
      return true;
    } else {
      this.renderer.addClass(this.elementRef.nativeElement, 'gas-upload__error');
      this.input.value = '';
      return false;
    }
  }

  /**
   * если elementRef.nativeElement.nodeName равен GAS-UPLOADER, значит мы имеем дело с компонентом uploadera
   * он всегда должен быть dragAndDrop
   */
  public setIsDragAndDropValue(): void {
    this.isDragAndDrop = this.elementRef.nativeElement.nodeName === GAS_UPLOADER;
  }


  public onChange = (value: File | File[]) => {
  };
  public onTouched = () => {
  };

  private getFileArray(dragFiles: FileList, eventFiles: FileList): File[] {
    if (!dragFiles && !eventFiles) {
      return [];
    }

    const files = dragFiles || eventFiles;

    return Array.from(files);
  }
}
